import { METAL_OPTION_NAME, RING_RANKED_SERIES } from '@shared/lib/constants';
import { ProductFilter } from '@shared/lib/shopify/types';
import { ShortJewelryProduct } from '@shared/lib/shopify/types/shopify-native';
import { assertKeyOf } from '@shared/lib/utils';
import threeStoneImg from '@shared/public/images/ring/3stones.jpg';
import bypassImg from '@shared/public/images/ring/bypass.jpg';
import contemporaryImg from '@shared/public/images/ring/contemporary.jpg';
import fancyImg from '@shared/public/images/ring/fancy.jpg';
import multiRowImg from '@shared/public/images/ring/multi_row.jpg';
import paveImg from '@shared/public/images/ring/pave.jpg';
import prongSetImg from '@shared/public/images/ring/prong_set.jpg';
import singleRowImg from '@shared/public/images/ring/single_row.jpg';
import solitaireImg from '@shared/public/images/ring/solitaires.jpg';
import twistedShankImg from '@shared/public/images/ring/twisted_shank.jpg';
import { StaticImageData } from 'next/image';

export const ProductTypes = ['engagement_ring', 'necklace', 'earring'];
export const ringOptions: Record<string, { label: string; img: StaticImageData }> = {
  solitaire: { label: 'Solitaire', img: solitaireImg },
  prong_set: { label: 'Prong Setting', img: prongSetImg },
  single_row: { label: 'Single Row', img: singleRowImg },
  multi_row: { label: 'Multi Row', img: multiRowImg },
  bypass: { label: 'Bypass', img: bypassImg },
  twisted_shank: { label: 'Twisted Shank', img: twistedShankImg },
  // halo: { label: 'Halo', img: haloImg },
  // hidden_halo: { label: 'Hidden Halo', img: hiddenHaloImg },
  // split_shank: { label: 'Split Shank', img: splitShankImg },
  three_stone: { label: 'Three Stone', img: threeStoneImg },
  contemporary: { label: 'Contemporary', img: contemporaryImg },
  // cluster: { label: 'Cluster', img: clusterImg },
  fancy: { label: 'Fancy', img: fancyImg },
  pave: { label: 'Pave', img: paveImg },
};
export const RingTypes = Object.keys(ringOptions);

// export const cutOptions: Record<string, { label: string; img: StaticImageData }> = {
//   round: { label: 'Round', img: roundImg },
//   princess: { label: 'Princess', img: princessImg },
//   emerald: { label: 'Emerald', img: emeraldImg },
//   oval: { label: 'Oval', img: ovalImg },
//   pear: { label: 'Pear', img: pearImg },
//   marquise: { label: 'Marquise', img: marquiseImg },
//   radiant: { label: 'Radiant', img: radiantImg },
//   cushion: { label: 'Cushion', img: cushionImg },
//   heart: { label: 'Heart', img: heartImg }
// };
// export const CutTypes = Object.keys(cutOptions);
export const MetalTypes = ['white_gold', 'yellow_gold', 'rose_gold', 'platinum'];

export function getProductFilterLabel(type: string, value: string) {
  switch (type) {
    case 'ring':
      return ringOptions[value]?.label;
    case 'cut':
      return value;
    case 'metal':
      return value.replace('_', ' ');
    default:
      throw new Error(`Unknown filter type: ${type}`);
  }
}

const TagsByRingType = {
  solitaire: ['Solitaires', 'Solitaires Plus'],
  halo: ['Halo', 'Double Halo', 'Hidden Halo'],
  hidden_halo: ['Hidden Halo'],
  single_row: ['Single Row'],
  multi_row: ['MultiRow'],
  twisted_shank: ['Twisted Shank'],
  prong_set: ['Prong Set'],
  split_shank: ['Split Shank Engagement Ring'],
  three_stone: ['3 Stone'],
  vintage: ['Vintage'],
  bypass: ['Bypass'],
  channel_set: ['Channel Set'],
  cluster: ['Cluster'],
  contemporary: ['Contemporary'],
  fancy: ['Fancy Shape Melee'],
  pave: ['Pave'],
};

const TagsByCutType: Record<string, string[]> = {
  round: ['Round'],
  square: ['Square', 'Princess'],
  marquise: ['Marquise'],
  pave: ['Pave'],
  cushion: ['Cushion'],
  oval: ['Oval'],
  emerald: ['Emerald'],
  princess: ['Square', 'Princess'],
  pear: ['Pear'],
  radiant: ['Radiant'],
  heart: ['Heart'],
  asscher: ['Asscher'],
  any: ['Take Peg Head'],
};

export const TagByMetalType = {
  yellow_gold: '14K yellow gold',
  white_gold: '14K white gold',
  rose_gold: '14K rose gold',
  platinum: 'Platinum',
};

export default function getProductQueryWFilters(
  type: string,
  ringTypes: string[] | undefined,
  cutTypes: string[] | undefined,
  metalTypes: string[] | undefined
) {
  const typeQuery = `product_type:"${type}"`;

  const ringQuery =
    ringTypes?.length &&
    ringTypes
      .flatMap((rt) => assertKeyOf(TagsByRingType, rt).map((tag) => `tag:"${tag}"`))
      .join(' OR ');

  const cutQuery =
    cutTypes?.length &&
    [...cutTypes, 'any']
      .flatMap((ct) => assertKeyOf(TagsByCutType, ct).map((tag) => `tag:"${tag}"`))
      .join(' OR ');

  const query = [typeQuery, ringQuery, cutQuery] //, metalQuery]
    .filter((query) => query) // Remove undefined values.
    .join(' AND '); // If present, all filters must match.

  const productFilters: ProductFilter[] =
    metalTypes?.map((metalType) => ({
      variantOption: { name: METAL_OPTION_NAME, value: assertKeyOf(TagByMetalType, metalType) },
    })) || [];

  return { query, productFilters };
}

export function reRankWithTags(products: ShortJewelryProduct[]) {
  const reRankedProducts: ShortJewelryProduct[] = [];

  RING_RANKED_SERIES.forEach((series) => {
    const seriesProducts = products.filter((product) => product.title.split(' ')[0] === series);
    reRankedProducts.push(...seriesProducts);
    products = products.filter((product) => product.title.split(' ')[0] !== series);
  });

  // Add remaining products to the result list
  reRankedProducts.push(...products);

  return reRankedProducts;
}
